import React, { useState, useRef, useEffect } from 'react';
import { Briefcase, Star, TrendingUp, Heart, CheckCircle2, ArrowRight, ArrowLeft, Upload } from 'lucide-react';

const useInView = (threshold = 0.1) => {
const ref = useRef(null);
const [inView, setInView] = useState(false);
useEffect(() => {
const observer = new IntersectionObserver(([entry]) => {
if (entry.isIntersecting) setInView(true);
}, { threshold });
if (ref.current) observer.observe(ref.current);
return () => observer.disconnect();
}, [threshold]);
return [ref, inView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
const [ref, inView] = useInView();
return (
<div ref={ref} className={className} style={{
opacity: inView ? 1 : 0,
transform: inView ? 'translateY(0)' : 'translateY(40px)',
transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`
}}>
{children}
</div>
);
};

const CareersPage = () => {
const [showForm, setShowForm] = useState(false);
const [step, setStep] = useState(1);
const [submitted, setSubmitted] = useState(false);
const [loading, setLoading] = useState(false);
const [cvFile, setCvFile] = useState(null);
const cvInputRef = useRef(null);
const [formData, setFormData] = useState({
experience: '',
teamwork: '',
situation: '',
motivation: '',
availability: '',
name: '',
email: '',
phone: '',
address: '',
coverLetter: '',
other: '',
cvFileName: ''
});

const questions = [
{
id: 'experience',
question: 'Vilken bakgrund har du?',
options: [
{ value: 'hospital', label: '🏥 Sjukhus eller vård', desc: 'Erfarenhet från högt reglerade hygieniska miljöer' },
{ value: 'school', label: '🏫 Skola eller offentlig sektor', desc: 'Erfarenhet från miljöer med strikta säkerhetskrav' },
{ value: 'hotel', label: '🏨 Hotell eller servicebranschen', desc: 'Erfarenhet av hög kvalitetsstandard och kundservice' },
{ value: 'other', label: '💼 Annan relevant erfarenhet', desc: 'Annan bakgrund med fokus på precision och kvalitet' }
]
},
{
id: 'teamwork',
question: 'Hur trivs du bäst med att arbeta?',
options: [
{ value: 'team', label: '👥 I ett tight, synkroniserat team', desc: 'Du älskar att arbeta i grupp med tydliga roller' },
{ value: 'independent', label: '🎯 Självständigt med tydliga mål', desc: 'Du trivs när du vet vad som förväntas' },
{ value: 'both', label: '⚡ Flexibelt — både och', desc: 'Du anpassar dig efter situationen' },
{ value: 'leading', label: '👑 Som ledare i teamet', desc: 'Du motiveras av att koordinera och leda andra' }
]
},
{
id: 'situation',
question: 'Du städar ett hem och hittar en del som är svårare än förväntat. Vad gör du?',
options: [
{ value: 'extra_time', label: '⏰ Tar extra tid för att göra det rätt', desc: 'Kvalitet går alltid före hastighet' },
{ value: 'inform', label: '📢 Informerar teamledaren direkt', desc: 'Kommunikation är nyckeln' },
{ value: 'skip', label: '🏃 Hoppar över det och fortsätter', desc: 'Effektivitet är viktigast' },
{ value: 'problem_solve', label: '🔧 Löser det kreativt med tillgängliga verktyg', desc: 'Anpassningsförmåga är din styrka' }
]
},
{
id: 'motivation',
question: 'Vad motiverar dig mest i ett arbete?',
options: [
{ value: 'quality', label: '⭐ Att leverera högsta möjliga kvalitet', desc: 'Du strävar alltid efter perfektion' },
{ value: 'growth', label: '📈 Möjlighet till karriärutveckling', desc: 'Du vill växa och bli bättre' },
{ value: 'team_spirit', label: '🤝 Starkt lagkamratskap', desc: 'Du motiveras av din omgivning' },
{ value: 'pride', label: '💪 Stolthet i välgjort arbete', desc: 'Känslan av att ha gjort ett bra jobb' }
]
},
{
id: 'availability',
question: 'Hur ser din tillgänglighet ut för behovsanställning?',
options: [
{ value: 'always', label: '📱 Alltid tillgänglig när ni ringer', desc: 'Jag kan rycka in med kort varsel' },
{ value: 'weekdays', label: '📅 Vardagar i första hand', desc: 'Jag är mest tillgänglig mån-fre' },
{ value: 'weekends', label: '🗓️ Helger i första hand', desc: 'Jag är mest tillgänglig lör-sön' },
{ value: 'flexible', label: '✨ Flexibel — bestämmer från fall till fall', desc: 'Jag svarar ja eller nej beroende på tillfälle' }
]
}
];

const currentQuestion = questions[step - 1];

const handleOptionSelect = (questionId, value) => {
setFormData(prev => ({ ...prev, [questionId]: value }));
};

const handleCvUpload = (e) => {
const file = e.target.files[0];
if (file) {
setCvFile(file);
setFormData(p => ({ ...p, cvFileName: file.name }));
}
};

const handleSubmit = async () => {
setLoading(true);
try {
// Convert CV to base64 if uploaded
let cvBase64 = '';
let cvName = formData.cvFileName;
if (cvFile) {
cvBase64 = await new Promise((resolve) => {
const reader = new FileReader();
reader.onload = () => resolve(reader.result.split(',')[1]);
reader.readAsDataURL(cvFile);
});
}

await fetch('/.netlify/functions/send-application', {
method: 'POST',
headers: { 'Content-Type': 'application/json' },
body: JSON.stringify({ ...formData, cvBase64, cvName })
});
setSubmitted(true);
} catch (error) {
console.error('Error:', error);
setSubmitted(true);
}
setLoading(false);
};

if (!showForm) {
return (
<div style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e' }}>
{/* Hero */}
<section className="relative py-32" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
<div className="absolute inset-0 opacity-10" style={{
backgroundImage: `url('https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=1600&q=80')`,
backgroundSize: 'cover', backgroundPosition: 'center'
}}/>
<div className="absolute top-0 left-0 w-full h-1" style={{ background: 'linear-gradient(90deg, transparent, #C9A84C, transparent)' }}/>
<div className="relative z-10 text-center text-white px-4">
<p className="text-sm font-bold tracking-widest mb-4" style={{ color: '#C9A84C' }}>KARRIÄR</p>
<h1 className="text-5xl md:text-6xl font-bold mb-6">Bli en del av <span style={{ color: '#C9A84C' }}>GALAX</span></h1>
<p className="text-xl max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.8)' }}>
Vi söker inte bara städare — vi söker framtidens elitspecialister.
</p>
</div>
</section>

{/* Benefits */}
<section className="py-24 px-4" style={{ backgroundColor: '#fff' }}>
<div className="max-w-6xl mx-auto">
<AnimatedSection className="text-center mb-16">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>VARFÖR JOBBA HOS OSS</p>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Mer Än Ett Jobb</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
{[
{ icon: <Star className="w-8 h-8"/>, title: 'Bli Specialist', desc: 'Du är inte "bara en städare" — du blir en tränad specialist med verklig expertis.' },
{ icon: <TrendingUp className="w-8 h-8"/>, title: 'Karriärutveckling', desc: 'Möjlighet att bli teamledare, specialist eller träna nya teammedlemmar.' },
{ icon: <Heart className="w-8 h-8"/>, title: 'Stolthet', desc: 'Jobba i ett team där kvalitet värderas och ditt arbete uppskattas.' },
{ icon: <Briefcase className="w-8 h-8"/>, title: 'Timlön', desc: 'Du får betalt för varje timme du jobbar. Behovsanställning med timlön.' }
].map((benefit, idx) => (
<AnimatedSection key={idx} delay={idx * 0.1}>
<div className="p-8 rounded-2xl text-center h-full"
style={{ backgroundColor: '#f8f7f4', border: '1px solid #e8e4d9' }}>
<div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
style={{ backgroundColor: '#1a1a2e' }}>
<div style={{ color: '#C9A84C' }}>{benefit.icon}</div>
</div>
<h3 className="font-bold text-lg mb-2" style={{ color: '#1a1a2e' }}>{benefit.title}</h3>
<p className="text-sm" style={{ color: '#777' }}>{benefit.desc}</p>
</div>
</AnimatedSection>
))}
</div>

{/* What is behovsanställning */}
<AnimatedSection className="mb-16">
<div className="p-8 rounded-2xl" style={{ backgroundColor: '#1a1a2e' }}>
<h3 className="text-2xl font-bold mb-4" style={{ color: '#C9A84C' }}>💡 Vad betyder behovsanställning?</h3>
<p className="text-lg mb-4" style={{ color: 'rgba(255,255,255,0.8)' }}>
Som behovsanställd hos GALAX jobbar du när vi har uppdrag och du har möjlighet.
</p>
<ul className="space-y-3" style={{ color: 'rgba(255,255,255,0.7)' }}>
<li className="flex items-start gap-3">
<span style={{ color: '#C9A84C' }}>✓</span>
<span>Vi ringer när ett uppdrag finns — du bestämmer om du kan ta det</span>
</li>
<li className="flex items-start gap-3">
<span style={{ color: '#C9A84C' }}>✓</span>
<span>Du får betalt per timme för varje pass du jobbar</span>
</li>
<li className="flex items-start gap-3">
<span style={{ color: '#C9A84C' }}>✓</span>
<span>Perfekt som extrainkomst eller vid sidan av annat jobb</span>
</li>
<li className="flex items-start gap-3">
<span style={{ color: '#C9A84C' }}>✓</span>
<span>Ju mer tillgänglig du är, desto fler pass får du</span>
</li>
</ul>
</div>
</AnimatedSection>

{/* Process */}
<AnimatedSection className="text-center mb-12">
<p className="text-sm font-bold tracking-widest mb-3" style={{ color: '#C9A84C' }}>REKRYTERINGSPROCESSEN</p>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Hur Det Fungerar</h2>
<div className="w-16 h-1 mx-auto" style={{ backgroundColor: '#C9A84C' }}/>
</AnimatedSection>
<div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-16">
{[
{ step: '1', title: 'Ansökan', desc: 'Fyll i vår digitala ansökan' },
{ step: '2', title: 'Telefonintervju', desc: 'Vi pratar om din bakgrund' },
{ step: '3', title: 'Praktisk Test', desc: 'Visa dina färdigheter' },
{ step: '4', title: 'Träning', desc: 'Vid behov upp till 6 veckor' },
{ step: '5', title: 'Välkommen!', desc: 'Du är nu elitspecialist' }
].map((phase, idx) => (
<AnimatedSection key={idx} delay={idx * 0.1} className="text-center">
<div className="w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-3"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
{phase.step}
</div>
<h4 className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{phase.title}</h4>
<p className="text-sm" style={{ color: '#888' }}>{phase.desc}</p>
</AnimatedSection>
))}
</div>

{/* CTA */}
<AnimatedSection>
<div className="p-12 rounded-2xl text-center" style={{ backgroundColor: '#1a1a2e' }}>
<h2 className="text-3xl font-bold mb-4 text-white">Redo Att Söka?</h2>
<p className="mb-8" style={{ color: 'rgba(255,255,255,0.7)' }}>
Fyll i vår ansökan på 5 minuter och vi återkommer inom kort.
</p>
<button onClick={() => setShowForm(true)}
className="px-10 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 flex items-center space-x-2 mx-auto"
style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
<span>Ansök Nu</span>
<ArrowRight className="w-5 h-5"/>
</button>
</div>
</AnimatedSection>
</div>
</section>
</div>
);
}

if (submitted) {
return (
<div className="min-h-screen flex items-center justify-center px-4 pt-32"
style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-lg w-full text-center">
<div className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
style={{ backgroundColor: '#C9A84C' }}>
<CheckCircle2 className="w-12 h-12" style={{ color: '#1a1a2e' }} />
</div>
<h2 className="text-4xl font-bold mb-4" style={{ color: '#1a1a2e' }}>Ansökan Skickad!</h2>
<p className="text-lg mb-4" style={{ color: '#555' }}>
Tack {formData.name}! Vi har mottagit din ansökan och skickat en bekräftelse till {formData.email}.
</p>
<p style={{ color: '#888' }}>Vi återkommer inom kort. 🌟</p>
<button onClick={() => { setShowForm(false); setStep(1); setSubmitted(false); setCvFile(null); }}
className="mt-8 px-8 py-3 rounded-full font-bold transition-all hover:scale-105"
style={{ backgroundColor: '#1a1a2e', color: '#C9A84C', border: '2px solid #C9A84C' }}>
Tillbaka till Karriär
</button>
</div>
</div>
);
}

return (
<div className="min-h-screen pt-32 pb-20 px-4" style={{ backgroundColor: '#f8f7f4' }}>
<div className="max-w-2xl mx-auto">
{/* Progress */}
<div className="mb-8">
<div className="flex items-center justify-between mb-2">
<span className="text-sm font-semibold" style={{ color: '#C9A84C' }}>
{step <= 5 ? `Fråga ${step} av 5` : 'Dina uppgifter'}
</span>
<span className="text-sm" style={{ color: '#888' }}>{Math.round((step / 6) * 100)}%</span>
</div>
<div className="h-2 rounded-full" style={{ backgroundColor: '#e8e4d9' }}>
<div className="h-2 rounded-full transition-all"
style={{ width: `${(step / 6) * 100}%`, backgroundColor: '#C9A84C' }}/>
</div>
</div>

<div className="p-8 rounded-2xl"
style={{ backgroundColor: '#fff', border: '1px solid #e8e4d9', boxShadow: '0 4px 30px rgba(0,0,0,0.08)' }}>

{/* Questions 1-5 */}
{step <= 5 && (
<div>
<h2 className="text-2xl font-bold mb-8" style={{ color: '#1a1a2e' }}>
{currentQuestion.question}
</h2>
<div className="space-y-4">
{currentQuestion.options.map((option) => (
<button key={option.value}
onClick={() => handleOptionSelect(currentQuestion.id, option.value)}
className="w-full p-4 rounded-xl text-left transition-all"
style={{
border: formData[currentQuestion.id] === option.value ? '2px solid #C9A84C' : '2px solid #e8e4d9',
backgroundColor: formData[currentQuestion.id] === option.value ? 'rgba(201,168,76,0.1)' : '#fff'
}}>
<div className="font-bold mb-1" style={{ color: '#1a1a2e' }}>{option.label}</div>
<div className="text-sm" style={{ color: '#888' }}>{option.desc}</div>
</button>
))}
</div>
<div className="flex space-x-4 mt-8">
{step > 1 && (
<button onClick={() => setStep(step - 1)}
className="flex-1 py-3 rounded-full font-bold flex items-center justify-center space-x-2"
style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-4 h-4"/><span>Tillbaka</span>
</button>
)}
<button onClick={() => setStep(step + 1)}
disabled={!formData[currentQuestion.id]}
className="flex-1 py-3 rounded-full font-bold flex items-center justify-center space-x-2 transition-all"
style={{
backgroundColor: formData[currentQuestion.id] ? '#C9A84C' : '#e8e4d9',
color: formData[currentQuestion.id] ? '#1a1a2e' : '#aaa'
}}>
<span>Nästa</span><ArrowRight className="w-4 h-4"/>
</button>
</div>
</div>
)}

{/* Step 6: Personal Details */}
{step === 6 && (
<div>
<h2 className="text-2xl font-bold mb-2" style={{ color: '#1a1a2e' }}>Dina Uppgifter</h2>
<p className="mb-6" style={{ color: '#888' }}>Nästan klart! Fyll i dina kontaktuppgifter.</p>
<div className="space-y-4">
<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Namn *</label>
<input type="text" value={formData.name}
onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
placeholder="Anna Andersson"
className="w-full px-4 py-3 rounded-xl focus:outline-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Telefon *</label>
<input type="tel" value={formData.phone}
onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
placeholder="070-XXX XX XX"
className="w-full px-4 py-3 rounded-xl focus:outline-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>
</div>
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Email *</label>
<input type="email" value={formData.email}
onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
placeholder="anna@example.com"
className="w-full px-4 py-3 rounded-xl focus:outline-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Adress</label>
<input type="text" value={formData.address}
onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
placeholder="Gatan 1, Motala"
className="w-full px-4 py-3 rounded-xl focus:outline-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>

{/* CV Upload */}
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>
CV <span style={{ color: '#888', fontWeight: 'normal' }}>(valfritt)</span>
</label>
<input
type="file"
ref={cvInputRef}
onChange={handleCvUpload}
accept=".pdf,.doc,.docx"
className="hidden"/>
<div
onClick={() => cvInputRef.current.click()}
className="flex items-center space-x-3 p-4 rounded-xl cursor-pointer transition-all hover:scale-101"
style={{ border: `2px dashed ${cvFile ? '#16a34a' : '#C9A84C'}`, backgroundColor: cvFile ? '#dcfce7' : '#f8f7f4' }}>
<Upload className="w-5 h-5 flex-shrink-0" style={{ color: cvFile ? '#16a34a' : '#C9A84C' }}/>
<div>
<p className="font-semibold text-sm" style={{ color: cvFile ? '#16a34a' : '#1a1a2e' }}>
{cvFile ? `✅ ${cvFile.name}` : 'Klicka för att ladda upp CV'}
</p>
<p className="text-xs" style={{ color: '#aaa' }}>PDF, DOC eller DOCX • Max 5MB</p>
</div>
</div>
</div>

<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Personligt brev *</label>
<textarea value={formData.coverLetter}
onChange={(e) => setFormData(p => ({ ...p, coverLetter: e.target.value }))}
placeholder="Berätta varför du vill jobba hos GALAX och vad du kan bidra med..."
rows={4} className="w-full px-4 py-3 rounded-xl focus:outline-none resize-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>
<div>
<label className="block text-sm font-bold mb-1" style={{ color: '#1a1a2e' }}>Övrigt</label>
<textarea value={formData.other}
onChange={(e) => setFormData(p => ({ ...p, other: e.target.value }))}
placeholder="Något annat du vill tillägga?"
rows={2} className="w-full px-4 py-3 rounded-xl focus:outline-none resize-none"
style={{ border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e' }}/>
</div>
</div>

<div className="flex space-x-4 mt-8">
<button onClick={() => setStep(5)}
className="flex-1 py-3 rounded-full font-bold flex items-center justify-center space-x-2"
style={{ backgroundColor: '#f8f7f4', color: '#1a1a2e', border: '2px solid #e8e4d9' }}>
<ArrowLeft className="w-4 h-4"/><span>Tillbaka</span>
</button>
<button onClick={handleSubmit}
disabled={!formData.name || !formData.email || !formData.phone || !formData.coverLetter || loading}
className="flex-1 py-3 rounded-full font-bold transition-all"
style={{
backgroundColor: formData.name && formData.email && formData.phone && formData.coverLetter ? '#C9A84C' : '#e8e4d9',
color: formData.name && formData.email && formData.phone && formData.coverLetter ? '#1a1a2e' : '#aaa'
}}>
{loading ? 'Skickar...' : '🚀 Skicka Ansökan'}
</button>
</div>
</div>
)}
</div>
</div>
</div>
);
};

export default CareersPage;